import { faCity } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import MatchScoreBadge from '../match-score-badge/MatchScoreBadge'
import {
  formatBedSummary,
  formatMatchSummary,
  formatPriceRange,
  formatSquareFootageRange,
  formatStatusLabel,
} from './helpers'
import {
  Address,
  BadgeRow,
  Body,
  CardRoot,
  Footer,
  ImageElement,
  ImagePlaceholder,
  ImageWrapper,
  ManagedBy,
  MatchSummary,
  Meta,
  Price,
  StatusBadge,
  Title,
  TitleRow,
} from './styled'
import { DiscoverBuildingCardProps } from './types'

/**
 * Building-level card for the discover grid. Each card is one building
 * (group of matched inventory rows). Price, beds, and square-footage
 * collapse to ranges across the matched units. Click navigates to the
 * detail page where the renter can drill into individual units.
 *
 * @param {DiscoverBuildingCardProps} props
 * @returns {JSX.Element}
 */
const DiscoverBuildingCard = ({
  building,
  filtersInUse,
}: DiscoverBuildingCardProps): JSX.Element => {
  const detailPath = `/discover/${encodeURIComponent(building.id)}`
  const firstImage = building.imageUrls[0]
  const bedSummary = formatBedSummary(building)
  const sqftSummary = formatSquareFootageRange(building)
  const metaSegments = [bedSummary, sqftSummary].filter(part => part !== '')
  const linkState =
    filtersInUse === null ? undefined : { filters: filtersInUse }

  return (
    <CardRoot>
      <ImageWrapper to={detailPath} state={linkState}>
        {firstImage !== undefined ? (
          <ImageElement
            src={firstImage}
            alt={building.buildingName}
            loading="lazy"
          />
        ) : (
          <ImagePlaceholder aria-hidden="true">
            <Icon icon={faCity} label="No photo" />
          </ImagePlaceholder>
        )}
        <BadgeRow>
          <StatusBadge tone={building.availabilitySummary}>
            {formatStatusLabel(building.availabilitySummary)}
          </StatusBadge>
        </BadgeRow>
      </ImageWrapper>

      <Body>
        <TitleRow>
          <Title to={detailPath} state={linkState}>
            {building.buildingName}
          </Title>
          <MatchScoreBadge score={building.matchScore} size="sm" />
        </TitleRow>
        <Address>
          {building.compiledAddress !== ''
            ? building.compiledAddress
            : building.city}
        </Address>
        <MatchSummary>{formatMatchSummary(building)}</MatchSummary>
        <Price>{formatPriceRange(building)}</Price>
        {metaSegments.length > 0 ? (
          <Meta>{metaSegments.join(' · ')}</Meta>
        ) : null}
        <Footer>
          {building.propertyManagerName !== '' ? (
            <ManagedBy>by {building.propertyManagerName}</ManagedBy>
          ) : (
            <span />
          )}
        </Footer>
      </Body>
    </CardRoot>
  )
}

export default DiscoverBuildingCard
